import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import {
  Sparkles,
  Image,
  Send,
  RefreshCw,
  Check,
  Facebook,
  Instagram,
  Linkedin,
  Wand2,
  Download,
  Building2,
  Plane,
  Heart
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";

// Brand icons and colors
const brandConfig = {
  "MAAVI": { icon: Building2, color: "bg-blue-500", textColor: "text-blue-500" },
  "TripMate AI": { icon: Plane, color: "bg-emerald-500", textColor: "text-emerald-500" },
  "Solo&Co": { icon: Heart, color: "bg-pink-500", textColor: "text-pink-500" }
};

const NetworkIcon = ({ network, className }) => {
  const icons = {
    facebook: Facebook,
    instagram: Instagram,
    linkedin: Linkedin
  };
  const Icon = icons[network];
  return Icon ? <Icon className={className} /> : null;
};

const NetworkPreview = ({ network, content, image }) => {
  const colors = {
    facebook: "border-[#1877F2]",
    instagram: "border-[#E4405F]",
    linkedin: "border-[#0A66C2]"
  };

  return (
    <Card className={`border-l-4 ${colors[network]}`}>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <NetworkIcon network={network} className="w-4 h-4" />
          {network.charAt(0).toUpperCase() + network.slice(1)}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {image && (
          <div className="mb-3 rounded-lg overflow-hidden">
            <img 
              src={(image.startsWith('data:') || image.startsWith('http')) ? image : `data:image/png;base64,${image}`} 
              alt="Generated" 
              className="w-full h-48 object-cover"
            />
          </div>
        )}
        <p className="text-sm whitespace-pre-wrap">{content || "Le contenu apparaîtra ici..."}</p>
      </CardContent>
    </Card>
  );
};

export default function ContentCreator() {
  const [activeBrand, setActiveBrand] = useState(null);
  const [theme, setTheme] = useState("");
  const [additionalContext, setAdditionalContext] = useState("");
  const [selectedNetworks, setSelectedNetworks] = useState(["facebook", "instagram"]);
  const [generatedPosts, setGeneratedPosts] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generateWithImage, setGenerateWithImage] = useState(false);
  const [imagePromptForContent, setImagePromptForContent] = useState("");
  const [generatedImage, setGeneratedImage] = useState(null);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [imagePrompt, setImagePrompt] = useState("");
  const [isGeneratingImage, setIsGeneratingImage] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [activeTab, setActiveTab] = useState("content");

  useEffect(() => {
    fetchActiveBrand();
  }, []);

  const fetchActiveBrand = async () => {
    try {
      const response = await axios.get(`${API}/brands/active`);
      setActiveBrand(response.data);
    } catch (error) {
      console.error("Error fetching active brand:", error);
    }
  };

  const toggleNetwork = (network) => {
    setSelectedNetworks(prev =>
      prev.includes(network)
        ? prev.filter(n => n !== network)
        : [...prev, network]
    );
  };

  const handleUploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    setIsUploading(true);
    try {
      const response = await axios.post(`${API}/media/upload`, formData);
      if (response.data.success) {
        setUploadedImage({ url: response.data.url, filename: response.data.filename });
        toast.success("Image chargée !");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'upload de l'image");
    } finally {
      setIsUploading(false);
      e.target.value = "";
    }
  };

  const handleGenerateContent = async () => {
    if (!theme.trim()) {
      toast.error("Veuillez entrer un thème");
      return;
    }

    if (selectedNetworks.length === 0) {
      toast.error("Veuillez sélectionner au moins un réseau");
      return;
    }

    setIsGenerating(true);
    setGeneratedPosts([]);

    try {
      // Utiliser l'endpoint avec image si l'option est activée
      const endpoint = generateWithImage ? "/content/generate-with-image" : "/content/generate";
      
      const response = await axios.post(`${API}${endpoint}`, {
        brand_id: activeBrand?.id,
        theme: theme,
        networks: selectedNetworks,
        additional_context: additionalContext || null,
        generate_image: generateWithImage,
        image_url: uploadedImage?.url || null,
        image_prompt: imagePromptForContent || `Image pour ${theme} - ${activeBrand?.name || 'MAAVI'}`
      });

      setGeneratedPosts(response.data.posts || []);
      
      const message = generateWithImage && response.data.has_image 
        ? `${response.data.posts?.length || 0} posts générés avec image ! 🖼️` 
        : `${response.data.posts?.length || 0} posts générés !`;
      
      toast.success(message);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la génération");
    } finally {
      setIsGenerating(false);
    }
  };

  const handleGenerateImage = async () => {
    if (!imagePrompt.trim()) {
      toast.error("Veuillez entrer une description pour l'image");
      return;
    }

    setIsGeneratingImage(true);

    try {
      const response = await axios.post(`${API}/content/generate-image`, {
        prompt: imagePrompt,
        style: "professional marketing, modern, clean"
      });

      if (response.data.success && response.data.image_base64) {
        setGeneratedImage(response.data.image_base64);
        toast.success("Image générée avec succès !");
      } else {
        toast.info("Mode simulation - pas d'image générée");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la génération d'image");
    } finally {
      setIsGeneratingImage(false);
    }
  };

  const handleGenerateFromTemplate = async () => {
    if (!selectedTemplate) {
      toast.error("Aucun template sélectionné");
      return;
    }

    setIsGeneratingImage(true);

    try {
      const response = await axios.post(`${API}/content/generate-image`, {
        prompt: imagePrompt,
        style: `professional marketing, modern, clean, ${activeBrand?.name || 'MAAVI'}`,
        template: selectedTemplate
      });

      if (response.data.success && response.data.image_base64) {
        setGeneratedImage(response.data.image_base64);
        toast.success("Image générée avec succès !");
      } else {
        toast.info("Mode simulation - pas d'image générée");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la génération d'image");
    } finally {
      setIsGeneratingImage(false);
    }
  };

  const handleDownloadImage = () => {
    if (!generatedImage) return;
    
    const link = document.createElement('a');
    link.href = `data:image/png;base64,${generatedImage}`;
    link.download = `${activeBrand?.name || 'maavi'}-image.png`;
    link.click();
  };

  return (
    <div className="space-y-6 animate-slide-up" data-testid="content-creator">
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-1 max-w-md">
          <TabsTrigger value="content" data-testid="tab-content">
            <Sparkles className="w-4 h-4 mr-2" />
            Contenu IA
          </TabsTrigger>
        </TabsList>

        {/* Content Generation Tab */}
        <TabsContent value="content" className="space-y-6 mt-6">
          {/* Active Brand Indicator */}
          {activeBrand && (
            <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50 border">
              {(() => {
                const config = brandConfig[activeBrand.name] || brandConfig["MAAVI"];
                const BrandIcon = config.icon;
                return (
                  <>
                    <div className={`w-8 h-8 rounded-full ${config.color} flex items-center justify-center`}>
                      <BrandIcon className="w-4 h-4 text-white" />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium">{activeBrand.name}</p>
                      <p className="text-xs text-muted-foreground">
                        Ton: {activeBrand.tone?.substring(0, 50)}...
                      </p>
                    </div>
                    <Badge variant="outline" className={config.textColor}>
                      Marque active
                    </Badge>
                  </>
                );
              })()}
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Input Form */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Wand2 className="w-5 h-5 text-primary" />
                  Paramètres de génération
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="theme">Thème / Sujet *</Label>
                  <Input
                    id="theme"
                    placeholder="Ex: Les avantages du travail indépendant"
                    value={theme}
                    onChange={(e) => setTheme(e.target.value)}
                    data-testid="theme-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="context">Contexte additionnel (optionnel)</Label>
                  <Textarea
                    id="context"
                    placeholder="Ex: Mentionner notre nouveau service, ton humoristique..."
                    value={additionalContext}
                    onChange={(e) => setAdditionalContext(e.target.value)}
                    rows={3}
                    data-testid="context-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Réseaux sociaux</Label>
                  <div className="grid grid-cols-2 gap-3">
                    {["facebook", "instagram"].map((network) => (
                      <div
                        key={network}
                        className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-all
                          ${selectedNetworks.includes(network) 
                            ? 'border-primary bg-primary/10' 
                            : 'border-border hover:border-muted-foreground'
                          }`}
                        onClick={() => toggleNetwork(network)}
                        data-testid={`network-toggle-${network}`}
                      >
                        <Checkbox
                          checked={selectedNetworks.includes(network)}
                          onCheckedChange={() => toggleNetwork(network)}
                        />
                        <NetworkIcon network={network} className="w-4 h-4" />
                        <span className="text-sm capitalize">{network}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Upload d'image manuelle */}
                <div className="space-y-3 p-4 bg-muted/30 rounded-lg">
                  <Label className="flex items-center gap-2">
                    <Image className="w-4 h-4 text-secondary" />
                    Ajouter une image (PNG, JPG, WEBP - max 8 Mo)
                  </Label>
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    onChange={handleUploadImage}
                    disabled={isUploading}
                    className="block w-full text-sm text-muted-foreground file:mr-3 file:px-3 file:py-1.5 file:rounded-md file:border-0 file:bg-primary file:text-primary-foreground file:cursor-pointer"
                    data-testid="media-upload-input"
                  />
                  {isUploading && (
                    <p className="text-xs text-muted-foreground">Envoi en cours...</p>
                  )}
                  {uploadedImage && (
                    <div className="space-y-2">
                      <img src={uploadedImage.url} alt="Image chargée" className="max-h-40 rounded-lg border" />
                      <Button type="button" variant="outline" size="sm" onClick={() => setUploadedImage(null)}>
                        Retirer l&apos;image
                      </Button>
                    </div>
                  )}
                </div>

                <Button
                  className="w-full btn-primary"
                  onClick={handleGenerateContent}
                  disabled={isGenerating}
                  data-testid="generate-content-btn"
                >
                  {isGenerating ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      {generateWithImage ? "Génération contenu + image..." : "Génération en cours..."}
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      {generateWithImage ? "Générer contenu + image" : "Générer le contenu"}
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>

            {/* Previews */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Aperçu du contenu</h3>
              {selectedNetworks.length === 0 ? (
                <div className="empty-state py-12 border border-dashed border-border rounded-xl">
                  <Send className="empty-state-icon" />
                  <p>Sélectionnez au moins un réseau social</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {selectedNetworks.map((network) => {
                    const post = generatedPosts.find(p => p.network === network);
                    return (
                      <NetworkPreview
                        key={network}
                        network={network}
                        content={post?.content}
                        image={post?.image_base64 || generatedImage || (uploadedImage ? uploadedImage.url : null)}
                      />
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        {/* Image Generation Tab */}
      </Tabs>
    </div>
  );
}
